import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Confirm,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Table,
  Segment,
} from 'semantic-ui-react';
import styled from 'styled-components';
import useAudiobookGeneration, {
  AudiobookStatusChapter,
} from 'src/hooks/useAudiobookGeneration';
import FeedbackButton from 'src/components/feedback/FeedbackButton';
import AudiobookParagraphStudio from './audiobookParagraphStudio';

// Stable, UNTRANSLATED tag stored on every audiobook feedback row so the
// `Feedback` collection can be filtered by feature without splitting per UI
// language (the tooltip/label may localize, the stored tag must not).
const AUDIOBOOK_FEEDBACK_FEATURE = 'Audiobook';

/**
 * The access-ON state of the author Audiobook panel: an explicit view to
 * GENERATE the audiobook (with the cost visible) and PREVIEW/listen to the
 * result — so it's obvious how to produce audio and what it costs, instead of
 * dropping the author into the reader. Reuses the existing generation +
 * streaming endpoints (see hooks/useAudiobookGeneration.ts). Real credits are
 * charged on generation (20 credits / 1,000 words per chapter).
 */

const CREDIT_USD = 0.1; // 1 credit = $0.10 (creditPricing.ts peg)

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

const ReaderAccessCard = styled.section<{ $published: boolean }>`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin: 22px 0 4px;
  padding: 15px 17px;
  border: 1px solid ${({ $published }) => ($published ? '#c9b8ef' : '#ded9d0')};
  border-left: 4px solid
    ${({ $published }) => ($published ? '#6435c9' : '#9a958c')};
  border-radius: 8px;
  background: ${({ $published }) => ($published ? '#f8f5ff' : '#faf9f7')};

  @media (max-width: 580px) {
    grid-template-columns: auto minmax(0, 1fr);
    padding: 14px;

    > label {
      grid-column: 1 / -1;
      justify-self: stretch;
    }
  }
`;

const AccessState = styled.span<{ $published: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $published }) => ($published ? '#6435c9' : '#9a958c')};
  box-shadow: 0 0 0 4px
    ${({ $published }) => ($published ? '#e9e0fb' : '#ece9e3')};
`;

const AccessToggle = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  min-height: 38px;
  color: var(--br-text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  input {
    appearance: none;
    position: relative;
    width: 38px;
    height: 22px;
    margin: 0;
    border: 1px solid #aaa49a;
    border-radius: 999px;
    background: #dedad3;
    cursor: pointer;
    transition:
      background 140ms ease,
      border-color 140ms ease;
  }

  input::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--br-surface);
    box-shadow: 0 1px 3px rgba(51, 45, 62, 0.28);
    transition: transform 140ms ease;
  }

  input:checked {
    border-color: #6435c9;
    background: #6435c9;
  }

  input:checked::after {
    transform: translateX(16px);
  }

  input:focus-visible {
    outline: 3px solid rgba(100, 53, 201, 0.3);
    outline-offset: 3px;
  }

  input:disabled {
    cursor: wait;
  }

  @media (prefers-reduced-motion: reduce) {
    input,
    input::after {
      transition: none;
    }
  }
`;

const usd = (credits: number) =>
  `$${(Math.round(credits * CREDIT_USD * 100) / 100).toLocaleString()}`;

type Props = {
  bookId?: string;
  contentVersionId?: string;
  bookTitle?: string;
  narratorName?: string;
  embedded?: boolean;
  canManageReaderAccess?: boolean;
  generationEnabled?: boolean;
  unavailableFallback?: React.ReactNode;
};

const StageTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
`;

const StageNumber = styled.span`
  display: inline-grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 50%;
  background: #6435c9;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
`;

const QueueTable = styled(Table)`
  @media (max-width: 580px) {
    &&& thead {
      display: none;
    }

    &&& tbody,
    &&& tr {
      display: block;
      width: 100%;
    }

    &&& tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 8px 12px;
      padding: 15px 0;
      border-bottom: 1px solid #e4e0da;
    }

    &&& td {
      display: block;
      width: auto !important;
      padding: 0 !important;
      border: 0 !important;
    }

    &&& td:nth-child(1) {
      grid-column: 1 / -1;
      font-weight: 700;
    }

    &&& td:nth-child(2),
    &&& td:nth-child(3) {
      color: var(--br-muted);
      text-align: left;
    }

    &&& td:nth-child(3) {
      text-align: right;
    }

    &&& td:nth-child(5) {
      text-align: right;
    }
  }
`;

const statusLabel = (
  t: (k: string, d?: string) => string,
  chapter?: AudiobookStatusChapter
) => {
  const status = chapter?.status;
  const timedOut =
    chapter?.error === 'PROVIDER_TIMEOUT' ||
    chapter?.error === 'STALE_TIMEOUT' ||
    /\btimeout(?: of \d+ms)? exceeded\b/i.test(chapter?.error || '') ||
    /\btimed out\b/i.test(chapter?.error || '');
  switch (status) {
    case 'draft':
      return <Label size="tiny" color="violet">In studio</Label>;
    case 'ready':
      return (
        <Label size="tiny" color="green">
          {t('AudiobookStatusReady', 'Ready')}
        </Label>
      );
    case 'generating':
      return (
        <Label size="tiny" color="blue">
          {t('AudiobookStatusGenerating', 'Generating…')}
        </Label>
      );
    case 'queued':
      return (
        <Label size="tiny" color="grey">
          {t('AudiobookStatusQueued', 'Queued')}
        </Label>
      );
    case 'error':
      return (
        <Label size="tiny" color="red">
          {timedOut
            ? t('AudiobookStatusTimedOut', 'Timed out')
            : t('AudiobookStatusError', 'Error')}
        </Label>
      );
    default:
      return (
        <Label size="tiny" basic>
          {t('AudiobookStatusNone', 'Not generated')}
        </Label>
      );
  }
};

const AudiobookGenerationPanel: React.FC<Props> = ({
  bookId,
  contentVersionId,
  bookTitle,
  narratorName,
  embedded = false,
  canManageReaderAccess = true,
  generationEnabled = true,
  unavailableFallback = null,
}) => {
  const { t } = useTranslation();
  const {
    preview,
    previewLoading,
    previewError,
    statusLoading,
    statusByPart,
    convert,
    getStreamUrl,
    readerAccess,
    setReaderAccess,
    refetchStatus,
  } = useAudiobookGeneration(bookId, contentVersionId, {
    generationEnabled,
  });

  const [previewPartId, setPreviewPartId] = React.useState<string | null>(null);
  const [studioPartId, setStudioPartId] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewErr, setPreviewErr] = React.useState<string | null>(null);
  const [generatingPartId, setGeneratingPartId] = React.useState<string | null>(
    null
  );
  // Pending credit spend awaiting confirmation. Every chapter, regeneration,
  // and whole-book request charges real credits. null = nothing pending.
  const [pendingGen, setPendingGen] = React.useState<{
    partIds: string[];
    singlePartId?: string;
    credits: number;
    title: string;
  } | null>(null);
  // Whether the "generation not set up yet" dialog is showing. Opened when a
  // convert fails with GENERATION_UNAVAILABLE (the server has no TTS provider),
  // dismissable — this is NOT a user error, so it must not read as a red banner.
  const [unavailableOpen, setUnavailableOpen] = React.useState(false);

  const chapters = preview?.chapters || [];
  const billable = chapters.filter(c => c.hasText && !statusByPart[c.partId]?.inStudio);
  const totalCredits = billable.reduce((sum, chapter) => sum + chapter.credits, 0);
  const balance = preview?.credits?.balance;
  const insufficient = typeof balance === 'number' && balance < totalCredits;

  const playPreview = async (partId: string) => {
    setPreviewErr(null);
    setPreviewPartId(partId);
    setPreviewUrl(null);
    try {
      const { url } = await getStreamUrl(partId);
      setPreviewUrl(url);
    } catch (e: any) {
      setPreviewErr(
        e?.message || t('AudiobookPreviewFailed', 'Could not load audio.')
      );
    }
  };

  const generate = async (partIds: string[], singlePartId?: string) => {
    setGeneratingPartId(singlePartId || '__all__');
    try {
      await convert.mutateAsync(partIds);
    } catch (e: any) {
      // GENERATION_UNAVAILABLE (server has no TTS provider) isn't a user error —
      // open the gentle "being set up" dialog. Opening HERE, on each attempt,
      // means a re-click after dismissing re-shows it (an effect keyed on the
      // error would not, since the code stays the same). Other errors surface in
      // the banner via `convert.error`. Swallow the rejection either way so the
      // onClick promise doesn't become an unhandled rejection.
      if (e?.code === 'GENERATION_UNAVAILABLE') setUnavailableOpen(true);
    } finally {
      setGeneratingPartId(null);
    }
  };

  const renderReaderAccessControl = () => {
    if (
      !canManageReaderAccess ||
      (!readerAccess.published && readerAccess.readyChapterCount === 0)
    ) {
      return null;
    }

    return (
      <ReaderAccessCard
        $published={readerAccess.published}
        data-testid="audiobook-reader-access"
      >
        <>
          <AccessState $published={readerAccess.published} aria-hidden="true" />
          <div>
            <>
              <div style={{ fontWeight: 800, color: 'var(--br-text)' }}>
                {readerAccess.published
                  ? t('AudiobookReaderAccessOn', 'Available to readers')
                  : t('AudiobookReaderAccessOff', 'Private')}
              </div>
              <div style={{ color: 'var(--br-muted)', fontSize: 12, marginTop: 2 }}>
                {readerAccess.published
                  ? t(
                      'AudiobookReaderAccessOnHelp',
                      '{{n}} ready chapters can be listened to in the reader app.',
                      { n: readerAccess.readyChapterCount }
                    )
                  : t(
                      'AudiobookReaderAccessOffHelp',
                      'Only you can preview the {{n}} ready chapters until you make them available.',
                      { n: readerAccess.readyChapterCount }
                    )}
              </div>
              {readerAccess.published && readerAccess.hasUnpublishedAudio && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ color: 'var(--br-muted)', fontSize: 12, marginBottom: 6 }}>
                    {t('AudiobookNewAudioPrivate',
                      'New audio is ready. Readers still hear the previously published version.')}
                  </div>
                  <Button
                    size="mini"
                    type="button"
                    disabled={setReaderAccess.isLoading}
                    onClick={() => {
                      Promise.resolve(setReaderAccess.mutateAsync(true)).catch(
                        () => undefined
                      );
                    }}
                  >
                    {t('AudiobookPublishLatestAudio', 'Publish latest audio')}
                  </Button>
                </div>
              )}
              {setReaderAccess.error && (
                <div
                  role="alert"
                  style={{ color: '#b03060', fontSize: 12, marginTop: 5 }}
                >
                  {(setReaderAccess.error as any).message}
                </div>
              )}
            </>
          </div>
          <AccessToggle>
            <input
              type="checkbox"
              checked={readerAccess.published}
              disabled={setReaderAccess.isLoading}
              onChange={event => {
                // Promise.resolve also tolerates the lightweight mutation mock
                // used by the focused component test.
                Promise.resolve(
                  setReaderAccess.mutateAsync(event.target.checked)
                ).catch(() => undefined);
              }}
              data-testid="audiobook-reader-access-toggle"
              aria-label={t(
                'AudiobookReaderAccessToggle',
                'Make ready audiobook chapters available to readers'
              )}
            />
            {setReaderAccess.isLoading
              ? t('Saving', 'Saving…')
              : t('AudiobookReaderAccessToggleLabel', 'Reader access')}
          </AccessToggle>
        </>
      </ReaderAccessCard>
    );
  };

  if (studioPartId && bookId && contentVersionId && generationEnabled) {
    return <AudiobookParagraphStudio
      bookId={bookId}
      contentVersionId={contentVersionId}
      partId={studioPartId}
      onClose={() => { setStudioPartId(null); refetchStatus(); }}
    />;
  }

  if (!generationEnabled) {
    if (statusLoading) {
      return (
        <Segment basic textAlign="center" style={{ padding: 40 }}>
          <Loader active inline>
            {t('AudiobookLoadingExisting', 'Loading existing audiobook…')}
          </Loader>
        </Segment>
      );
    }

    if (!readerAccess.published && readerAccess.readyChapterCount === 0) {
      return <>{unavailableFallback}</>;
    }

    return (
      <div data-testid="audiobook-existing-management">
        <StageTitle>
          <Icon name="headphones" />
          <span>{t('AudiobookExisting', 'Existing audiobook')}</span>
          <Label size="tiny" color="violet">
            {t('BetaLabel', 'Beta')}
          </Label>
        </StageTitle>
        <p style={{ color: 'var(--br-muted)', marginTop: 8, maxWidth: 560 }}>
          {t(
            'AudiobookGenerationOffExistingHelp',
            'New generation is currently paused. Your existing audio remains available to preview and manage.'
          )}
        </p>
        {renderReaderAccessControl()}
      </div>
    );
  }

  if (previewLoading) {
    return (
      <Segment basic textAlign="center" style={{ padding: 40 }}>
        <Loader active inline>
          {t('AudiobookLoadingCost', 'Estimating cost…')}
        </Loader>
      </Segment>
    );
  }

  // A 402 here means the feature is on but the account can't afford the whole
  // book; we still render the table so per-chapter generation is possible.
  const convertErr: any = convert.error;
  // Keep GENERATION_UNAVAILABLE out of the red banner — it's handled by the
  // gentle dialog opened in `generate`'s catch (on the click), never a banner.
  const generationUnavailable = convertErr?.code === 'GENERATION_UNAVAILABLE';

  return (
    <div data-testid="audiobook-generation-panel">
      <Header>
        <div>
          <StageTitle>
            {embedded && <StageNumber>2</StageNumber>}
            <span>
              {!embedded && <Icon name="headphones" />}{' '}
              {embedded
                ? t('AudiobookProduction', 'Generate your audiobook')
                : t('Audiobook', 'Audiobook')}
            </span>
            <Label
              size="tiny"
              color="violet"
              style={{ verticalAlign: 'middle' }}
            >
              {t('BetaLabel', 'Beta')}
            </Label>
            {/* Feature feedback (mirrors critiqueGroupList). The STORED tag is a
                constant — never t(...) — so reports don't split by language. */}
            <FeedbackButton
              featureName={AUDIOBOOK_FEEDBACK_FEATURE}
              url={window.location.pathname}
            />
          </StageTitle>
          <p style={{ color: 'var(--br-muted)', marginTop: 6, maxWidth: 560 }}>
            {embedded
              ? `Narrated by ${narratorName || 'your selected voice'}. Generate the book in one pass, or open a chapter studio to direct individual paragraphs. Each path shows its own cost before charging.`
              : t(
                  'AudiobookGenerateIntro',
                  'Generate “{{title}}” in one pass, or open a chapter studio to direct individual paragraphs. Each path shows its own cost before charging.',
                  { title: bookTitle || t('YourBook', 'your book') }
                )}
          </p>
        </div>
        <Segment compact style={{ margin: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--br-muted)' }}>
            {t('AudiobookOnePassWholeBook', 'Whole book · one pass')}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {usd(totalCredits)}{' '}
            <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--br-muted)' }}>
              ·{' '}
              {t('AudiobookCredits', '{{n}} credits', {
                n: totalCredits.toLocaleString(),
              })}
            </span>
          </div>
          {typeof balance === 'number' && (
            <div
              style={{
                fontSize: 12,
                color: insufficient ? '#c0392b' : '#6b675e',
                marginTop: 4,
              }}
            >
              {t('AudiobookBalance', 'Balance: {{n}} credits', {
                n: balance.toLocaleString(),
              })}
            </div>
          )}
        </Segment>
      </Header>

      {previewError && (
        <Message negative size="tiny">
          {previewError.code === 'FEATURE_DISABLED'
            ? t(
                'AudiobookFeatureDisabled',
                'Audiobook generation is not enabled for your account.'
              )
            : previewError.message}
        </Message>
      )}

      {insufficient && (
        <Message
          warning
          size="tiny"
          data-testid="audiobook-insufficient-credits"
        >
          <Icon name="warning circle" />
          {t(
            'AudiobookInsufficientCredits',
            'Not enough credits for the whole book. You can still generate individual chapters, or top up your credits.'
          )}
        </Message>
      )}

      {/* GENERATION_UNAVAILABLE is NOT a user error — it opens the dialog below,
          so keep it out of the red banner. */}
      {convertErr && !generationUnavailable && (
        <Message negative size="tiny">
          {convertErr.code === 'LANGUAGE_NOT_SUPPORTED'
            ? t(
                'AudiobookLanguageUnsupported',
                'Audiobook generation currently supports English and Swedish only.'
              )
            : convertErr.code === 'INSUFFICIENT_CREDITS'
              ? t(
                  'AudiobookInsufficientForSelection',
                  'Not enough credits for that selection.'
                )
              : convertErr.message}
        </Message>
      )}

      {renderReaderAccessControl()}

      <h3 style={{ margin: '22px 0 4px' }}>
        {t('AudiobookProductionQueue', 'Production queue')}
      </h3>
      <div style={{ color: 'var(--br-muted)', fontSize: 12, marginBottom: 8 }}>
        One-pass chapter prices are shown here. Open a studio for paragraph prices and direction controls. Package the book from the studio when every chapter is ready.
      </div>
      <QueueTable basic="very" compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t('Chapter', 'Chapter')}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              {t('Words', 'Words')}
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              {t('AudiobookOnePassCost', 'One-pass cost')}
            </Table.HeaderCell>
            <Table.HeaderCell>{t('Status', 'Status')}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {chapters.map(c => {
            const st = statusByPart[c.partId];
            const isReady = st?.status === 'ready';
            const inStudio = Boolean(st?.inStudio);
            const inProgress =
              st?.status === 'queued' || st?.status === 'generating';
            const rowBusy =
              (convert.isLoading && generatingPartId === c.partId) ||
              inProgress;
            return (
              <Table.Row
                key={c.partId}
                data-testid={`audiobook-chapter-${c.partId}`}
              >
                <Table.Cell>{c.title}</Table.Cell>
                <Table.Cell textAlign="right">
                  {c.wordCount.toLocaleString()}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  {c.hasText ? (
                    <>
                      {usd(c.credits)}{' '}
                      <span style={{ color: 'var(--br-muted)', fontSize: 12 }}>
                        ({c.credits})
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--br-muted)' }}>—</span>
                  )}
                </Table.Cell>
                <Table.Cell>{statusLabel(t, st)}</Table.Cell>
                <Table.Cell textAlign="right">
                  {c.hasText && <Button
                    size="tiny"
                    basic
                    disabled={rowBusy}
                    onClick={() => setStudioPartId(c.partId)}
                    data-testid={`audiobook-studio-${c.partId}`}
                  >
                    <Icon name="edit" /> Open studio
                  </Button>}
                  {inStudio ? (isReady ? <Button size="tiny" basic onClick={() => playPreview(c.partId)}>
                    <Icon name="play" /> Preview chapter
                  </Button> : null) : isReady ? (
                    <>
                      <Button
                        size="tiny"
                        icon
                        labelPosition="left"
                        onClick={() => playPreview(c.partId)}
                        data-testid={`audiobook-preview-${c.partId}`}
                      >
                        <Icon name="play" />
                        {t('AudiobookPreview', 'Preview')}
                      </Button>
                      <Button
                        size="tiny"
                        basic
                        loading={rowBusy}
                        disabled={rowBusy}
                        onClick={() =>
                          setPendingGen({
                            partIds: [c.partId],
                            singlePartId: c.partId,
                            credits: c.credits,
                            title: c.title,
                          })
                        }
                      >
                        {t('AudiobookRegenerate', 'Regenerate')} ·{' '}
                        {usd(c.credits)}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="tiny"
                      color="violet"
                      disabled={!c.hasText || rowBusy}
                      loading={rowBusy}
                      onClick={() =>
                        setPendingGen({
                          partIds: [c.partId],
                          singlePartId: c.partId,
                          credits: c.credits,
                          title: c.title,
                        })
                      }
                      data-testid={`audiobook-generate-${c.partId}`}
                    >
                      {inProgress
                        ? t('AudiobookStatusGenerating', 'Generating…')
                        : `${t('AudiobookGenerateChapter', 'Generate')} · ${usd(
                            c.credits
                          )}`}
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </QueueTable>

      {previewPartId && (
        <Segment
          data-testid="audiobook-preview-player"
          style={{ marginTop: 8 }}
        >
          <div style={{ fontSize: 13, color: 'var(--br-muted)', marginBottom: 8 }}>
            {t('AudiobookNowPreviewing', 'Previewing:')}{' '}
            {chapters.find(c => c.partId === previewPartId)?.title}
          </div>
          {previewErr ? (
            <Message negative size="tiny">
              {previewErr}
            </Message>
          ) : previewUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              controls
              autoPlay
              src={previewUrl}
              style={{ width: '100%' }}
            />
          ) : (
            <Loader active inline size="small" />
          )}
        </Segment>
      )}

      <div style={{ marginTop: 16 }}>
        <Button
          color="violet"
          disabled={!billable.length || convert.isLoading}
          loading={convert.isLoading && generatingPartId === '__all__'}
          onClick={() =>
            setPendingGen({
              partIds: billable.map(c => c.partId),
              credits: totalCredits,
              title: bookTitle || t('YourBook', 'your book'),
            })
          }
          data-testid="audiobook-generate-all"
        >
          <Icon name="magic" />
          {t('AudiobookGenerateAll', 'Generate audiobook')} ·{' '}
          {usd(totalCredits)}
        </Button>
        <span style={{ color: 'var(--br-muted)', fontSize: 12, marginLeft: 10 }}>
          {t(
            'AudiobookServeFree',
            'Generating costs credits; listening is free.'
          )}
        </span>
      </div>

      {/* Confirm real credit spends (whole-book + regenerate) before charging. */}
      <Confirm
        open={pendingGen != null}
        data-testid="audiobook-confirm-generate"
        header={t('AudiobookConfirmTitle', 'Generate audiobook?')}
        content={t(
          'AudiobookConfirmBody',
          'This will charge {{credits}} credits ({{usd}}) to your BetaReader balance and narrate “{{title}}”. Listening is free once generated.',
          {
            credits: (pendingGen?.credits ?? 0).toLocaleString(),
            usd: usd(pendingGen?.credits ?? 0),
            title: pendingGen?.title ?? '',
          }
        )}
        cancelButton={t('Cancel', 'Cancel')}
        confirmButton={
          <Button color="violet" data-testid="audiobook-confirm-go">
            {t('AudiobookConfirmGo', 'Confirm and generate')}
          </Button>
        }
        onCancel={() => setPendingGen(null)}
        onConfirm={() => {
          const p = pendingGen;
          setPendingGen(null);
          if (p) generate(p.partIds, p.singlePartId);
        }}
      />

      {/* Generation not switched on server-side yet. The user IS in the beta
          (they have access), so this is an informative "we're setting it up"
          message — NOT a "request access" prompt and NOT a hard error. */}
      <Modal
        open={unavailableOpen}
        onClose={() => setUnavailableOpen(false)}
        size="tiny"
        data-testid="audiobook-unavailable-dialog"
      >
        <Modal.Header>
          {t(
            'AudiobookUnavailableTitle',
            'Audiobook generation is being set up'
          )}
        </Modal.Header>
        <Modal.Content>
          <p>
            {t(
              'AudiobookUnavailableBody',
              'You’re in the audiobook beta, but generation isn’t switched on on our side just yet. We’re finishing the setup and will let you know the moment it’s ready — nothing you need to do.'
            )}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <FeedbackButton
            featureName={AUDIOBOOK_FEEDBACK_FEATURE}
            url={window.location.pathname}
            trigger={
              <Button basic data-testid="audiobook-unavailable-feedback">
                {t('AudiobookShareFeedback', 'Share feedback')}
              </Button>
            }
          />
          <Button
            color="violet"
            onClick={() => setUnavailableOpen(false)}
            data-testid="audiobook-unavailable-ok"
          >
            {t('OK', 'OK')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default AudiobookGenerationPanel;
